import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { setDeliveryMode, deleteSlotInCart } from '@api/interfaces/cartApi';
import trackPickUpInStoreSelection from '@helpers/analyticsHelpers/trackPickUpInStoreSelection';
import webToAppApi from '@api/web-to-app';
import logger from '@logger';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useAbortController from '@hooks/useAbortController';

export default function useFinishPickUpSubmission(theme: string, afterSubmit?: () => void) {
  const { refreshCart } = useCart();
  const checkCartConflict = useCheckCartConflict();
  const getSignal = useAbortController();
  const { store, refreshStore } = useStore();

  const handleStoreSwitch = async (deliveryStoreId: string) => {
    const signal = getSignal();
    const { activateStore } = await import('@api/interfaces/storeApi');
    const response = await activateStore(deliveryStoreId, true, false, signal);
    await refreshStore(response.data);
    await deleteSlotInCart(signal);
  };

  const updateState = () => {
    if (afterSubmit) afterSubmit();
  };

  const finishPickUpSubmission = async (deliveryStoreId: string, callback: () => void) => {
    try {
      await checkCartConflict({ storeId: deliveryStoreId });
      await setDeliveryMode('pickUpInStore', deliveryStoreId);
      if (store?.storeId !== deliveryStoreId || !store?.activelySelected) {
        await handleStoreSwitch(deliveryStoreId);
      }
      await refreshCart();
      updateState();
      trackPickUpInStoreSelection({ deliveryStoreId, theme });
      webToAppApi.eventDeliveryMethodChanged();
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Could not finish pickup submit', message: e.message });
      }
    } finally {
      if (callback) callback();
    }
  };

  return finishPickUpSubmission;
}
